import { TextField } from '@/global/components/TextField'
import type { CleaveOptions } from 'cleave.js/options'
import dayjs from 'dayjs'
import {
    type ChangeEvent,
    type FocusEventHandler,
    type ReactNode,
    useState,
} from 'react'

/** Formato do valor e do texto digitado. */
const FORMATO = 'HH:mm'

/**
 * Mesma máscara já usada na digitação do ponto
 * (`ManutencaoDoPonto/Digitacao/Input.tsx`) — é o gesto que os operadores
 * conhecem.
 */
const MASCARA: CleaveOptions = { time: true, timePattern: ['h', 'm'] }

export type TimeFieldProps = {
    /** Hora em `HH:mm`. String vazia é campo em branco. */
    value: string
    onChange?: (value: string) => void
    /** Chamado depois da correção interna do texto incompleto. */
    onBlur?: FocusEventHandler<HTMLInputElement>
    onFocus?: FocusEventHandler<HTMLInputElement>
    label?: ReactNode
    placeholder?: string
    helperText?: ReactNode
    error?: ReactNode | boolean
    unHelperText?: boolean
    required?: boolean
    disabled?: boolean
    readOnly?: boolean
    size?: 'small' | 'medium'
    variant?: 'filled' | 'outlined'
    /** Classe do wrapper (largura, coluna). */
    className?: string
    /** Classe da caixa do campo — é o `inputClassName` do `TextField`. */
    inputClassName?: string
    startAdornment?: ReactNode
    endAdornment?: ReactNode
    autoComplete?: string
    id?: string
    name?: string
    'data-testid'?: string
}

/**
 * Campo de hora: `TextField` com máscara `HH:mm`, sem popover — hora não tem
 * calendário, e um relógio em popover atrapalharia quem digita rápido.
 * Substitui o `<input type="time">` nativo, cujo seletor ignora o tema e muda
 * de idioma e de comportamento entre navegadores.
 *
 * Como no `DateField`, o texto fica em estado local enquanto está incompleto:
 * só vira `onChange` quando forma uma hora válida, e o que não formou volta ao
 * último valor válido ao sair do campo.
 */
export function TimeField({
    value,
    onChange,
    onBlur,
    onFocus,
    label,
    placeholder,
    helperText,
    error,
    unHelperText,
    required,
    disabled,
    readOnly,
    size = 'medium',
    variant = 'filled',
    className,
    inputClassName,
    startAdornment,
    endAdornment,
    autoComplete,
    id,
    name,
    'data-testid': dataTestId,
}: TimeFieldProps) {
    const bloqueado = !!readOnly || !!disabled

    // Texto em edição. O valor de fora continua mandando: quando ele muda, o
    // texto acompanha.
    const [texto, setTexto] = useState(value)
    const [textoSincronizado, setTextoSincronizado] = useState(value)
    if (value !== textoSincronizado) {
        setTextoSincronizado(value)
        setTexto(value)
    }

    const digitar = (evento: ChangeEvent<HTMLInputElement>) => {
        const novoTexto = evento.target.value
        setTexto(novoTexto)

        if (novoTexto === '') {
            if (value) onChange?.('')
            return
        }

        // Hora incompleta (`08:`) espera o resto da digitação — sem isto um
        // texto pela metade chegaria ao schema Zod.
        if (!dayjs(novoTexto, FORMATO, true).isValid()) return
        if (novoTexto === value) return

        onChange?.(novoTexto)
    }

    /** Texto que não formou hora volta a ser o último valor válido. */
    const sair: FocusEventHandler<HTMLInputElement> = evento => {
        if (texto !== '' && !dayjs(texto, FORMATO, true).isValid())
            setTexto(value)
        onBlur?.(evento)
    }

    return (
        <TextField
            id={id}
            name={name}
            data-testid={dataTestId}
            label={label}
            value={bloqueado ? value : texto}
            placeholder={placeholder}
            options={bloqueado ? undefined : MASCARA}
            onChange={bloqueado ? undefined : digitar}
            onBlur={sair}
            onFocus={onFocus}
            readOnly={readOnly}
            disabled={disabled}
            required={required}
            size={size}
            variant={variant}
            error={error}
            helperText={helperText}
            unHelperText={unHelperText}
            className={className}
            inputClassName={inputClassName}
            startAdornment={startAdornment}
            endAdornment={endAdornment}
            autoComplete={autoComplete}
        />
    )
}
